import { useObservabilityContext } from "@app/components/agent_builder/observability/ObservabilityContext";
import { TabContentChildSectionLayout } from "@app/components/agent_builder/observability/TabContentChildSectionLayout";
import { formatCreditsCompact } from "@app/lib/client/credits";
import { isNavigationLocked } from "@app/lib/navigation-lock";
import {
  useAgentAnalytics,
  useAgentObservabilitySummary,
} from "@app/lib/swr/assistants";
import type { LightWorkspaceType } from "@app/types/user";
import {
  Button,
  CardGrid,
  ContentMessage,
  LoadingBlock,
  SafeSuspense,
  Spinner,
  safeLazy,
  ValueCard,
} from "@dust-tt/sparkle";
import { useTranslation } from "react-i18next";

// Dynamic imports for chart components to exclude recharts from server bundle

const canReload = () => !isNavigationLocked();

const DatasourceRetrievalTreemapChart = safeLazy(
  () =>
    import(
      "@app/components/agent_builder/observability/charts/DatasourceRetrievalTreemapChart"
    ).then((mod) => ({
      default: mod.DatasourceRetrievalTreemapChart,
    })),
  { canReload }
);
const LatencyChart = safeLazy(
  () =>
    import(
      "@app/components/agent_builder/observability/charts/LatencyChart"
    ).then((mod) => ({
      default: mod.LatencyChart,
    })),
  { canReload }
);
const SourceChart = safeLazy(
  () =>
    import(
      "@app/components/agent_builder/observability/charts/SourceChart"
    ).then((mod) => ({
      default: mod.SourceChart,
    })),
  { canReload }
);
const SkillUsageChart = safeLazy(
  () =>
    import(
      "@app/components/agent_builder/observability/charts/SkillUsageChart"
    ).then((mod) => ({
      default: mod.SkillUsageChart,
    })),
  { canReload }
);
const ToolUsageChart = safeLazy(
  () =>
    import(
      "@app/components/agent_builder/observability/charts/ToolUsageChart"
    ).then((mod) => ({
      default: mod.ToolUsageChart,
    })),
  { canReload }
);
const ToolExecutionTimeChart = safeLazy(
  () =>
    import(
      "@app/components/agent_builder/observability/charts/ToolExecutionTimeChart"
    ).then((mod) => ({
      default: mod.ToolExecutionTimeChart,
    })),
  { canReload }
);
const UsageMetricsChart = safeLazy(
  () =>
    import(
      "@app/components/agent_builder/observability/charts/UsageMetricsChart"
    ).then((mod) => ({
      default: mod.UsageMetricsChart,
    })),
  { canReload }
);

function ChartFallback() {
  return <div className="h-64 animate-pulse rounded-lg bg-muted-background" />;
}

interface AgentObservabilityProps {
  owner: LightWorkspaceType;
  agentConfigurationId: string;
  isCustomAgent: boolean;
}

export function AgentObservability({
  owner,
  agentConfigurationId,
  isCustomAgent,
}: AgentObservabilityProps) {
  const { t } = useTranslation();
  const { period, mode, selectedVersion } = useObservabilityContext();

  const isTimeRangeMode = mode === "timeRange";

  const { agentAnalytics, isAgentAnalyticsLoading } = useAgentAnalytics({
    workspaceId: owner.sId,
    agentConfigurationId,
    period,
    version:
      isCustomAgent && mode === "version"
        ? selectedVersion?.version
        : undefined,
  });

  const shouldShowMessagesPerActiveUser =
    agentAnalytics?.mentions &&
    agentAnalytics.activeUsers > 0 &&
    agentAnalytics.mentions.messageCount > 0;

  const { summaryText, isSummaryLoading, isSummaryError, refetchSummary } =
    useAgentObservabilitySummary({
      workspaceId: owner.sId,
      agentConfigurationId,
      days: period,
      disabled: !isTimeRangeMode,
    });

  return (
    <div className="flex flex-col gap-6 pt-4">
      <TabContentChildSectionLayout
        title={t("builder.observabilitySectionOverview", "Overview")}
      >
        {isTimeRangeMode && (
          <div className="mb-4">
            {isSummaryLoading ? (
              <div className="bg-background rounded-lg border border-border p-4">
                <div className="mb-2 flex items-center justify-between">
                  <LoadingBlock className="h-5 w-24" />
                </div>
                <div className="space-y-2">
                  <LoadingBlock className="h-4 w-full" />
                  <LoadingBlock className="h-4 w-11/12" />
                  <LoadingBlock className="h-4 w-10/12" />
                </div>
              </div>
            ) : (
              <ContentMessage
                title={t("builder.observabilitySummaryTitle", "Summary")}
                variant="primary"
                size="lg"
                className="w-full"
              >
                {isSummaryError ? (
                  <div className="flex flex-col gap-2">
                    <span>
                      {t(
                        "builder.observabilitySummaryError",
                        "We couldn't generate a summary for this time range."
                      )}
                    </span>
                    <div>
                      <Button
                        label={t("builder.tryAgainButton", "Try again")}
                        size="xs"
                        variant="outline"
                        onClick={() => {
                          void refetchSummary();
                        }}
                      />
                    </div>
                  </div>
                ) : summaryText ? (
                  <p>{summaryText}</p>
                ) : (
                  <p>
                    {t(
                      "builder.observabilitySummaryEmpty",
                      "There is not enough activity in this time range to generate a summary yet."
                    )}
                  </p>
                )}
              </ContentMessage>
            )}
          </div>
        )}

        {isAgentAnalyticsLoading ? (
          <div className="w-full p-6">
            <Spinner />
          </div>
        ) : (
          <CardGrid>
            <ValueCard
              title={t("builder.activeUsersCardTitle", "Active Users")}
              className="h-24"
              content={
                <div className="flex flex-col gap-1 text-2xl">
                  {agentAnalytics?.activeUsers !== undefined ? (
                    <div className="truncate text-foreground">
                      {agentAnalytics.activeUsers}
                    </div>
                  ) : (
                    "-"
                  )}
                </div>
              }
            />
            <ValueCard
              title={t(
                "builder.messagesPerActiveUserCardTitle",
                "Messages / active user"
              )}
              className="h-24"
              content={
                <div className="flex flex-row gap-2 text-2xl">
                  {shouldShowMessagesPerActiveUser
                    ? `${Math.round(
                        agentAnalytics.mentions.messageCount /
                          agentAnalytics.activeUsers
                      )}`
                    : 0}
                </div>
              }
            />
            <div className="hidden @[40rem]:contents">
              <div></div>
            </div>

            <div className="hidden @[24rem]:contents">
              <ValueCard
                title={t("builder.totalCostCardTitle", "Total cost")}
                className="h-24"
                content={
                  <div className="flex flex-col gap-1 text-2xl">
                    <div className="truncate text-foreground">
                      {agentAnalytics?.costs?.totalCostCredits != null
                        ? t("builder.creditsAmount", "{{amount}} credits", {
                            amount: formatCreditsCompact(
                              agentAnalytics.costs.totalCostCredits
                            ),
                          })
                        : "-"}
                    </div>
                  </div>
                }
              />
            </div>
            <ValueCard
              title={t("builder.avgCostPerMsgCardTitle", "Avg. cost / msg")}
              className="h-24"
              content={
                <div className="flex flex-col gap-1 text-2xl">
                  <div className="truncate text-foreground">
                    {agentAnalytics?.costs?.avgCostCredits != null
                      ? t("builder.creditsAmount", "{{amount}} credits", {
                          amount: formatCreditsCompact(
                            agentAnalytics.costs.avgCostCredits
                          ),
                        })
                      : "-"}
                  </div>
                </div>
              }
            />
            <div className="hidden @[40rem]:contents">
              <ValueCard
                title={t(
                  "builder.medianCostPerMsgCardTitle",
                  "Median cost / msg"
                )}
                className="h-24"
                content={
                  <div className="flex flex-col gap-1 text-2xl">
                    <div className="truncate text-foreground">
                      {agentAnalytics?.costs?.medianCostCredits != null
                        ? t("builder.creditsAmount", "{{amount}} credits", {
                            amount: formatCreditsCompact(
                              agentAnalytics.costs.medianCostCredits
                            ),
                          })
                        : "-"}
                    </div>
                  </div>
                }
              />
            </div>
          </CardGrid>
        )}
      </TabContentChildSectionLayout>

      <TabContentChildSectionLayout
        title={t("builder.observabilitySectionDetails", "Details")}
      >
        <SafeSuspense fallback={<ChartFallback />}>
          <UsageMetricsChart
            workspaceId={owner.sId}
            agentConfigurationId={agentConfigurationId}
            isCustomAgent={isCustomAgent}
          />
        </SafeSuspense>
        <SafeSuspense fallback={<ChartFallback />}>
          <SourceChart
            workspaceId={owner.sId}
            agentConfigurationId={agentConfigurationId}
            isCustomAgent={isCustomAgent}
          />
        </SafeSuspense>
        <SafeSuspense fallback={<ChartFallback />}>
          <LatencyChart
            workspaceId={owner.sId}
            agentConfigurationId={agentConfigurationId}
            isCustomAgent={isCustomAgent}
          />
        </SafeSuspense>
        <SafeSuspense fallback={<ChartFallback />}>
          <DatasourceRetrievalTreemapChart
            workspaceId={owner.sId}
            agentConfigurationId={agentConfigurationId}
            isCustomAgent={isCustomAgent}
          />
        </SafeSuspense>
        <SafeSuspense fallback={<ChartFallback />}>
          <ToolUsageChart
            workspaceId={owner.sId}
            agentConfigurationId={agentConfigurationId}
            isCustomAgent={isCustomAgent}
          />
        </SafeSuspense>
        <SafeSuspense fallback={<ChartFallback />}>
          <SkillUsageChart
            workspaceId={owner.sId}
            agentConfigurationId={agentConfigurationId}
            isCustomAgent={isCustomAgent}
          />
        </SafeSuspense>
        <SafeSuspense fallback={<ChartFallback />}>
          <ToolExecutionTimeChart
            workspaceId={owner.sId}
            agentConfigurationId={agentConfigurationId}
            isCustomAgent={isCustomAgent}
          />
        </SafeSuspense>
      </TabContentChildSectionLayout>
    </div>
  );
}
